import {sortBy} from 'lodash';
import React, {Component} from 'react';
import {connect} from 'react-redux';

import {ExtractProps} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  AsyncMoreStatusesEnum,
  AsyncStatusesEnum,
} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {fetchApiAsync, postAsync} from '../../../../../../libs/shared/core/src/services/http/http';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {findSelectedInboxesFilterForKey} from '../../../../../helpers/modelHelpers';
import {
  ApiLegacyNotificationPage,
  LegacyNotificationModel,
} from '../../../../../models/api/legacyNotificationModel';
import {InboxesFiltersEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {updateSelectedFilterAction} from '../../../../../store/data/localPreferences/localPreferencesActions';
import {getSelectedFilters} from '../../../../../store/data/localPreferences/localPreferencesSelectors';
import {getUser} from '../../../../../store/data/teammates/teammatesSelectors';
import {NotificationCenterDropdown} from './notificationCenterDropdown';

/*
 * Component.
 */

interface NotificationCenterDropdownContainerProps extends ExtractProps<typeof Dropdown> {
  user: TeammateModel;
  selectedFilter: InboxesFiltersEnum;
  onFilterClick: (arg: InboxesFiltersEnum) => void;
  notificationsCounter: number;
}

interface NotificationCenterDropdownContainerState {
  status: AsyncStatusesEnum;
  statusMore: AsyncMoreStatusesEnum;
  notifications: ReadonlyArray<LegacyNotificationModel>;
  wasAllMarkedAsRead: boolean;
}

class NotificationCenterDropdownContainerComponent extends Component<
  NotificationCenterDropdownContainerProps,
  NotificationCenterDropdownContainerState
> {
  constructor(props: NotificationCenterDropdownContainerProps) {
    super(props);
    this.wasUnmounted = false;
    this.state = {
      status: AsyncStatusesEnum.LOADING,
      statusMore: AsyncMoreStatusesEnum.NONE,
      notifications: [],
      wasAllMarkedAsRead: false,
    };
  }

  private wasUnmounted: boolean;
  private fetchCancelSource?: CancelTokenSource;

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.fetchNotificationsAsync();
  }

  componentDidUpdate(previousProps: Readonly<NotificationCenterDropdownContainerProps>) {
    // If the filter didn't change, nothing to do.
    if (previousProps.selectedFilter === this.props.selectedFilter) {
      return;
    }

    // Otherwise, reload the notifications.
    this.fetchNotificationsAsync();
  }

  componentWillUnmount() {
    this.tryCancelFetch();
    this.wasUnmounted = true;
  }

  /*
   * Network.
   */

  private readonly fetchNotificationsAsync = async () => {
    this.setState({
      status: AsyncStatusesEnum.LOADING,
      statusMore: AsyncMoreStatusesEnum.NONE,
    });

    this.tryCancelFetch();
    this.fetchCancelSource = buildCancelTokenSource();

    try {
      const {user, selectedFilter} = this.props;
      const {token} = this.fetchCancelSource;

      const pathSuffix = selectedFilter === InboxesFiltersEnum.ALL ? '' : '/unseen';
      const path = `${user.url}/notifications${pathSuffix}`;
      const page = await fetchApiAsync(ApiLegacyNotificationPage, path, {cancelToken: token});

      const hasMore = page.notifications.length === page.limit;
      this.setState({
        status: AsyncStatusesEnum.LOADED,
        statusMore: hasMore ? AsyncMoreStatusesEnum.HAS_MORE : AsyncMoreStatusesEnum.ALL_LOADED,
        notifications: sortNotifications(page.notifications),
      });
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      this.setState({status: AsyncStatusesEnum.FAILED});
    }
  };

  private readonly fetchMoreNotificationsAsync = async () => {
    this.setState({statusMore: AsyncMoreStatusesEnum.LOADING});
    if (!this.fetchCancelSource) {
      return;
    }

    try {
      const {user, selectedFilter} = this.props;
      const {token} = this.fetchCancelSource;

      const pathSuffix = selectedFilter === InboxesFiltersEnum.ALL ? '' : '/unseen';
      const offset = this.state.notifications.length;
      const path = `${user.url}/notifications${pathSuffix}?offset=${offset}`;
      const page = await fetchApiAsync(ApiLegacyNotificationPage, path, {cancelToken: token});

      const hasMore = page.notifications.length === page.limit;
      this.setState(({notifications}) => ({
        statusMore: hasMore ? AsyncMoreStatusesEnum.HAS_MORE : AsyncMoreStatusesEnum.ALL_LOADED,
        notifications: sortNotifications([...notifications, ...page.notifications]),
      }));
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      this.setState({statusMore: AsyncMoreStatusesEnum.ERROR});
    }
  };

  private readonly markAllNotificationsAsReadAsync = async () => {
    // Update the state to mark everything as read.
    this.setState(({notifications}) => ({
      notifications: filterNotifications(
        notifications.map((n) => ({...n, isUnread: false})),
        this.props.selectedFilter,
      ),
      wasAllMarkedAsRead: true,
    }));

    // Perform the request.
    try {
      const path = `${this.props.user.url}/notifications/seen`;
      await postAsync(path, {});
    } catch (error) {
      // If the component was unmounted, nothing to do.
      if (this.wasUnmounted) {
        return;
      }

      // Otherwise, move to an error state.
      this.setState({status: AsyncStatusesEnum.FAILED});
    }
  };

  private readonly toggleMarkNotificationAsReadAsync: (model: LegacyNotificationModel) => void = async (
    notification,
  ) => {
    // Update the state right away.
    this.setState(({notifications}) => ({
      notifications: filterNotifications(
        notifications.map((n) => {
          if (n !== notification) {
            return n;
          }

          return {
            ...n,
            isUnread: !n.isUnread,
          };
        }),
        this.props.selectedFilter,
      ),
      wasAllMarkedAsRead: false,
    }));

    // Perform the request.
    try {
      const action = notification.isUnread ? 'seen' : 'unseen';
      const path = `${notification.url}/${action}`;

      await postAsync(path, {});
    } catch (error) {
      // If the component was unmounted, nothing to do.
      if (this.wasUnmounted) {
        return;
      }

      // Otherwise, move to an error state.
      this.setState({status: AsyncStatusesEnum.FAILED});
    }
  };

  private tryCancelFetch() {
    return this.fetchCancelSource && this.fetchCancelSource.cancel();
  }

  /*
   * Render.
   */

  render() {
    // Check if we should offer to mark everything as read.
    const {notificationsCounter} = this.props;
    const {notifications, wasAllMarkedAsRead} = this.state;
    const hasUnreadNotifications = notificationsCounter > 0 || notifications.some((n) => n.isUnread);
    const canMarkAllAsRead = hasUnreadNotifications && !wasAllMarkedAsRead;

    return (
      <NotificationCenterDropdown
        {...this.props}
        {...this.state}
        fetchMore={this.fetchMoreNotificationsAsync}
        canMarkAllAsRead={canMarkAllAsRead}
        onMarkAllAsReadClick={this.markAllNotificationsAsReadAsync}
        onToggleMarkAsReadClick={this.toggleMarkNotificationAsReadAsync}
        onRetryClick={this.fetchNotificationsAsync}
      />
    );
  }
}

function sortNotifications(notifications: ReadonlyArray<LegacyNotificationModel>) {
  return sortBy(notifications, (n) => -n.bumpedAt);
}

function filterNotifications(
  notifications: ReadonlyArray<LegacyNotificationModel>,
  filter: InboxesFiltersEnum,
) {
  if (filter === InboxesFiltersEnum.ALL) {
    return notifications;
  }

  return notifications.filter((n) => n.isUnread);
}

/*
 * Selectors.
 */

const notificationsFilterKey = 'notifications';
const getSelectedFilter = createSelector(
  [getSelectedFilters],
  (selectedFilters) =>
    findSelectedInboxesFilterForKey(selectedFilters, notificationsFilterKey) || InboxesFiltersEnum.ALL,
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  user: getUser,
  selectedFilter: getSelectedFilter,
}));

export const NotificationCenterDropdownContainer = connect(
  mapStateToProps,
  {updateSelectedFilter: updateSelectedFilterAction},
  (stateProps, dispatchProps, ownProps) => {
    const onFilterClick: (arg: InboxesFiltersEnum) => void = (filter) => {
      dispatchProps.updateSelectedFilter(notificationsFilterKey, filter);
    };

    return {
      ...ownProps,
      ...stateProps,
      onFilterClick,
    };
  },
)(NotificationCenterDropdownContainerComponent);
